import { SuggestionItem, SuggestionProps } from ".";

/**
 * Matching + ranking for the `@` / `#` suggestion popups.
 *
 * The previous implementation filtered at the *group* level with `.some()`, which
 * returned every child of a group whenever any single child matched — so typing
 * `@conden` still listed unrelated incidents. Everything here filters and ranks
 * individual children instead.
 */

/**
 * Below this length, a token only matches at the start of the value or of one of
 * its words. Allowing a 1-2 character token to match anywhere is too loose —
 * "condensate p" would otherwise keep "Condensate spill" alive on the "p" in
 * "spill" rather than narrowing to "Condensate pipeline".
 */
const MIN_SUBSTRING_TOKEN_LENGTH = 3;

/**
 * Typo tolerance kicks in only from this length. Allowing a single edit on short
 * tokens matches far too much ("sit" would reach "site", "silt", "list"…).
 */
const MIN_FUZZY_TOKEN_LENGTH = 4;

/**
 * True when `word` is within one edit (insert/delete/substitute) of `token`.
 * Bounded at distance 1 deliberately — it covers the realistic slips
 * ("insident", "employyee", "condesate") without the surprise matches a larger
 * edit budget produces on short business terms.
 */
const isWithinOneEdit = (word: string, token: string): boolean => {
  const lengthDiff = word.length - token.length;
  if (lengthDiff < -1 || lengthDiff > 1) return false;
  if (word === token) return true;

  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < word.length && j < token.length) {
    if (word[i] === token[j]) {
      i += 1;
      j += 1;
      continue;
    }
    if (++edits > 1) return false;
    // Advance whichever side is longer; on equal lengths treat it as a substitution.
    if (lengthDiff === 1) i += 1;
    else if (lengthDiff === -1) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  // Any unconsumed tail counts as the single remaining edit.
  return edits + (word.length - i) + (token.length - j) <= 1;
};

/** Fields a query is matched against, in the order they contribute to ranking. */
const scoreField = (field: string | undefined, token: string): number => {
  if (!field) return 0;
  const value = field.toLowerCase();
  if (value === token) return 100;
  if (value.startsWith(token)) return 70;
  const words = value.split(/[\s\-_/]+/);
  // Word-boundary hit, e.g. "spill" in "Condensate spill reaching Bayview Beach".
  if (words.some((word) => word.startsWith(token))) return 50;
  if (token.length >= MIN_SUBSTRING_TOKEN_LENGTH && value.includes(token))
    return 30;
  // Ranked below every exact form, so a real match always outranks a guess.
  if (
    token.length >= MIN_FUZZY_TOKEN_LENGTH &&
    words.some((word) => isWithinOneEdit(word, token))
  )
    return 15;
  return 0;
};

/**
 * Score a single suggestion against one whitespace-delimited token.
 * Returns 0 when the token matches nothing, which disqualifies the item.
 */
const scoreToken = (
  child: SuggestionItem,
  heading: string,
  token: string,
): number => {
  // Reference is weighted highest: users search by "INC-1782821234735" and expect
  // an exact hit to win over a title that merely contains the digits.
  const reference = scoreField(child.reference, token) * 1.2;
  const label = scoreField(child.label, token);
  const email = scoreField(child.email, token) * 0.9;
  // Lets users narrow by category — "@incident", "@employee", "@site", "@meeting".
  const modelType = scoreField(child.modelType, token) * 0.8;
  const category = scoreField(heading, token) * 0.7;
  // Singular form so "@meeting" matches the "Meetings" heading and "@employee"
  // matches "Employees".
  const categorySingular =
    heading.length > 1 && heading.toLowerCase().endsWith("s")
      ? scoreField(heading.slice(0, -1), token) * 0.7
      : 0;

  return Math.max(
    reference,
    label,
    email,
    modelType,
    category,
    categorySingular,
  );
};

/** Split a raw query into lowercase tokens, ignoring extra whitespace. */
export const tokenizeQuery = (query: string): string[] =>
  query.toLowerCase().trim().split(/\s+/).filter(Boolean);

/**
 * Score a suggestion against the whole query. Every token must match something
 * (AND semantics) so "condensate spill" narrows rather than widens.
 * Returns null when the item does not match.
 */
export const scoreSuggestion = (
  child: SuggestionItem,
  heading: string,
  tokens: string[],
): number | null => {
  if (tokens.length === 0) return 0;

  let total = 0;
  for (const token of tokens) {
    const score = scoreToken(child, heading, token);
    if (score === 0) return null;
    total += score;
  }
  return total;
};

/** Rank a single category's children against the query, dropping non-matches. */
export const filterChildren = (
  children: SuggestionItem[],
  heading: string,
  query: string,
): SuggestionItem[] => {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return children;

  return children
    .map((child) => ({ child, score: scoreSuggestion(child, heading, tokens) }))
    .filter(
      (entry): entry is { child: SuggestionItem; score: number } =>
        entry.score !== null,
    )
    // Stable-sort by score so the best match is first (and lands under the
    // keyboard cursor), preserving source order within equal scores.
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.child);
};

/**
 * Filter every category, dropping any that end up empty.
 * An empty query returns the groups untouched.
 */
export const filterSuggestionGroups = (
  groups: SuggestionProps[],
  query: string,
): SuggestionProps[] => {
  if (tokenizeQuery(query).length === 0) return groups;

  return groups
    .map((group) => ({
      ...group,
      children: filterChildren(group.children, group.heading, query),
    }))
    .filter((group) => group.children.length > 0);
};

/**
 * `allowSpaces` keeps the query growing as the user carries on writing prose after
 * an abandoned `@`. Past this point we stop treating the text as a search and let
 * the popup close rather than hang around showing "No results found" forever.
 */
export const isQueryAbandoned = (query: string): boolean =>
  query.length > 30 || tokenizeQuery(query).length > 4;
